import { ApplicationInsights } from '@microsoft/applicationinsights-web'

const CONSENT_KEY = 'analytics-consent'

let appInsights: ApplicationInsights | null = null

// ========================================
// LGPD Consent Management
// ========================================

/**
 * Checks if the user has given analytics consent (LGPD compliance).
 * Returns true only when the user explicitly accepted.
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(CONSENT_KEY) === 'true'
}

/**
 * Sets the user's analytics consent preference.
 * When consent is revoked, Application Insights is unloaded.
 */
export function setAnalyticsConsent(value: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(CONSENT_KEY, String(value))

  if (value) {
    // User accepted - initialize if not yet done
    initAppInsights()
  } else if (appInsights) {
    // User declined - stop tracking
    appInsights.unload(true)
    appInsights = null
  }
}

/**
 * Checks if the user has already made a consent choice (accept or decline).
 */
export function hasConsentChoice(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(CONSENT_KEY) !== null
}

// ========================================
// Application Insights Initialization
// ========================================

export function initAppInsights(): ApplicationInsights | null {
  if (typeof window === 'undefined') return null

  // Do not initialize if user has not given consent (LGPD)
  if (!hasAnalyticsConsent()) return null

  const connectionString = process.env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING

  if (!connectionString) {
    // TODO: When the instrumentation key / connection string is configured,
    // remove this fallback. For now, events are logged to console in dev.
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] Application Insights connection string not configured')
    }
    return null
  }

  if (appInsights) return appInsights

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      enableRequestHeaderTracking: true,
      enableAjaxPerfTracking: true,
      enableCorsCorrelation: true,
      correlationHeaderExcludedDomains: [],
      disableFetchTracking: false,
    },
  })

  appInsights.loadAppInsights()

  // Set cloud role
  appInsights.addTelemetryInitializer((envelope) => {
    if (envelope.tags) {
      envelope.tags['ai.cloud.role'] = 'portal-cliente-frontend'
      envelope.tags['ai.cloud.roleInstance'] = window.location.hostname
    }
  })

  return appInsights
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights
}

// ========================================
// Tracking Functions (consent-gated)
// ========================================

export function trackEvent(name: string, properties?: Record<string, string>) {
  if (!hasAnalyticsConsent()) return

  if (appInsights) {
    appInsights.trackEvent({ name, properties })
  } else {
    // TODO: Replace console.log with Application Insights once connection string is configured
    console.log('[Analytics Event]', name, properties)
  }
}

export function trackException(error: Error, properties?: Record<string, string>) {
  if (!hasAnalyticsConsent()) return

  if (appInsights) {
    appInsights.trackException({ exception: error, properties })
  } else {
    console.log('[Analytics Exception]', error.message, properties)
  }
}

export function trackPageView(name: string, uri?: string) {
  if (!hasAnalyticsConsent()) return

  if (appInsights) {
    appInsights.trackPageView({ name, uri })
  } else {
    console.log('[Analytics PageView]', name, uri)
  }
}
